import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@app/router/routes';
import { getUserInfo } from '@shared/api/client';
import { getMyProfile, getActivityList, getLightingPostList, getBuddhistList, getMascotList, getVowList, getDnaList, getBoardList, getPostcardDescribe, getExhibitionVowDescribe, getPointTxList, getShadowList, getExhibitionPostList, updateProfileAvatar } from '../api/profileService';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { usePageLayout } from '@app/router/index';
import RulePages from '@/features/rule/pages/RulePage';
import VowList from '../components/VowList';
import PostcardList from '../components/PostcardList';
import WishTreeList from '../components/WishTreeList';
import BoardDetailPopup from '../components/BoardDetailPopup';
import MemberNotificationBanner from '@shared/components/MemberNotificationBanner';
import { FEATURE_FLAGS } from '@shared/config/featureFlags';
import { useLanguage } from '@shared/contexts/LanguageContext';
import ActivityPointsTab from '../tabs/ActivityPointsTab';
import TreasureBagTab from '../tabs/TreasureBagTab';

// ─── 圓形吉祥物卡片 ───────────────────────────────────────────────────────────
function MascotCircle({ mascotUrl, mascotName, userName, size = 320, imgW = 340, imgH = 340, fontSize = 20 }) {
  const { t } = useLanguage();
  const initial = (userName || '').slice(-1) || t('profile.avatarInitialFallback');
  const circleBg = `
    linear-gradient(to bottom right, rgba(255,255,255,0.40) 0%, rgba(42,152,220,0.40) 12%, rgba(132,174,201,0.40) 18%, rgba(255,217,99,0.24) 24%, rgba(255,220,120,0.40) 30%, rgba(246,238,223,0.40) 44%, rgba(252,246,237,0.40) 50%) bottom right / 50% 50% no-repeat,
    linear-gradient(to bottom left,  rgba(255,255,255,0.40) 0%, rgba(42,152,220,0.40) 12%, rgba(132,174,201,0.40) 18%, rgba(255,217,99,0.24) 24%, rgba(255,220,120,0.40) 30%, rgba(246,238,223,0.40) 44%, rgba(252,246,237,0.40) 50%) bottom left  / 50% 50% no-repeat,
    linear-gradient(to top left,     rgba(255,255,255,0.40) 0%, rgba(42,152,220,0.40) 12%, rgba(132,174,201,0.40) 18%, rgba(255,217,99,0.24) 24%, rgba(255,220,120,0.40) 30%, rgba(246,238,223,0.40) 44%, rgba(252,246,237,0.40) 50%) top    left  / 50% 50% no-repeat,
    linear-gradient(to top right,    rgba(255,255,255,0.40) 0%, rgba(42,152,220,0.40) 12%, rgba(132,174,201,0.40) 18%, rgba(255,217,99,0.24) 24%, rgba(255,220,120,0.40) 30%, rgba(246,238,223,0.40) 44%, rgba(252,246,237,0.40) 50%) top    right / 50% 50% no-repeat
  `;
  const nameBottom = Math.round(size * 0.075);
  const imgTop = Math.round(size * 0.056);

  return (
    <div
      className="relative rounded-full flex-shrink-0"
      style={{
        width: size,
        height: size,
        background: circleBg,
        boxShadow: '0px 9px 19px rgba(196,182,171,0.10), 0px 35px 35px rgba(196,182,171,0.09), 0px 79px 48px rgba(196,182,171,0.05)',
      }}
    >
      {mascotUrl ? (
        <img
          src={mascotUrl}
          alt="mascot"
          className="object-contain"
          style={{
            width: imgW,
            height: imgH,
            position: 'absolute',
            left: '50%',
            top: imgTop,
            transform: 'translateX(-50%)',
            background: 'transparent',
          }}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-bold text-[#6E625B]" style={{ fontSize: fontSize * 3 }}>{initial}</span>
        </div>
      )}
      {mascotName && (
        <div
          className="absolute flex items-center justify-center"
          style={{
            bottom: nameBottom,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(255,255,255,0.60)',
            borderRadius: 999,
            paddingTop: 4,
            paddingBottom: 5,
            paddingLeft: 32,
            paddingRight: 32,
            boxShadow: '0px 11px 23px rgba(0,0,0,0.10), 0px 42px 42px rgba(0,0,0,0.09)',
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ color: '#6E625B', fontSize, fontFamily: 'Noto Sans TC', fontWeight: 400, lineHeight: `${fontSize * 1.6}px`, letterSpacing: 0.38 }}>
            {mascotName}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── 吉祥物區（Swiper 左右滑動） ─────────────────────────────────────────────
function MascotSection({ mascotUrl, mascotName, userName, collectedMascots = [], onAvatarUpdate }) {
  const { t } = useLanguage();
  const items = collectedMascots.length > 0 ? collectedMascots : [{
    reward_media: mascotUrl,
    reward_title: mascotName,
    reward_id: null
  }];

  const [activeIndex, setActiveIndex] = useState(() => {
    const idx = items.findIndex(it => (it.reward_media || it.mascot_url) === mascotUrl);
    return idx !== -1 ? idx : 0;
  });

  const activeItem = items[activeIndex] || items[0];
  const activeMedia = activeItem?.reward_media || activeItem?.mascot_url;
  const isDifferent = activeMedia && activeMedia !== mascotUrl && activeItem.reward_id;

  return (
    <div className="flex flex-col items-center w-full relative">
      <div className="relative w-full overflow-hidden" style={{ height: 320 }}>
        <Swiper
          initialSlide={activeIndex}
          centeredSlides={true}
          slidesPerView={'auto'}
          spaceBetween={-150}
          onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          className="w-full h-full"
        >
          {items.map((item, idx) => {
            const media = item.reward_media || item.mascot_url;
            const name = item.reward_title || item.mascot_name || item.category_x || mascotName;
            return (
              <SwiperSlide key={item.reward_id || idx} style={{ width: 320 }}>
                {({ isActive }) => (
                  <div style={{
                    transform: isActive ? 'scale(1)' : 'scale(0.3)',
                    opacity: isActive ? 1 : 0.4,
                    transition: 'all 0.6s',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <MascotCircle
                      mascotUrl={media}
                      mascotName={name}
                      userName={userName}
                      size={isActive ? 320 : 288}
                      imgW={isActive ? 162 : 146}
                      imgH={isActive ? 250 : 225}
                      fontSize={isActive ? 20 : 18}
                    />
                  </div>
                )}
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      {items.length > 1 && (
        <div className="flex justify-center items-center gap-1 mt-2 text-[#6E625B]/60 text-sm">
          <span className="material-symbols-outlined text-base">swipe</span>
          <span>{t('profile.mascotSwipeHint')}</span>
        </div>
      )}

      {isDifferent && (
        <div className="mt-4 flex justify-center w-full z-10">
          <button
            onClick={() => onAvatarUpdate && onAvatarUpdate(activeItem.reward_id)}
            className="flex flex-col justify-center items-center w-full active:scale-95 transition-transform"
          >
            <div className="self-stretch inline-flex justify-center items-center gap-2.5">
              <div className="px-2.5 py-1 bg-[#004682]/60 rounded-[20px] shadow-[inset_0px_2px_2px_0px_rgba(188,210,228,0.70)] flex justify-start items-center gap-1 overflow-hidden">
                <div className="justify-start text-white text-l font-bold font-['Noto_Serif_TC'] leading-8 tracking-tight">{t('profile.mascotSetAvatar')}</div>
              </div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── 積分進度條 ───────────────────────────────────────────────────────────────
function ScoreBar({ available, total }) {
  const { t } = useLanguage();
  const pct = total > 0 ? Math.min(100, (available / total) * 100) : 0;
  return (
    <div className="w-full" style={{ paddingTop: 16, paddingBottom: 8 }}>
      <div className="flex items-baseline justify-between mb-1">
        <span style={{ color: 'rgba(110,98,91,0.60)', fontSize: 13, fontFamily: 'Noto Sans TC', fontWeight: 400, lineHeight: '19.5px' }}>
          {t('profile.scoreLabel')}
        </span>
        <div className="flex items-baseline gap-1">
          <span style={{ color: '#C59B5A', fontSize: 20, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '32px', letterSpacing: 0.38 }}>
            {available.toLocaleString()}
          </span>
          <span style={{ color: 'rgba(110,98,91,0.30)', fontSize: 20, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '32px' }}>/</span>
          <span style={{ color: '#6E625B', fontSize: 20, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '32px', letterSpacing: 0.38 }}>
            {total.toLocaleString()}
          </span>
        </div>
      </div>
      <div className="rounded-full overflow-hidden" style={{ height: 8, background: 'rgba(110,98,91,0.12)' }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: '#BC313D' }}
        />
      </div>
    </div>
  );
}

// ─── 基本資料欄位 ─────────────────────────────────────────────────────────────
function InfoRow({ label, value, required, showEmpty, emptyText }) {
  if (!value && !showEmpty) return null;
  return (
    <div
      className="flex items-center gap-3"
      style={{
        alignSelf: 'stretch',
        height: 50,
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 4,
        paddingBottom: 4,
        background: 'white',
        borderRadius: 999,
        outline: '1px rgba(23,23,23,0.10) solid',
        outlineOffset: '-1px',
      }}
    >
      <div className="flex items-center flex-shrink-0">
        <span style={{ color: 'rgba(110,98,91,0.50)', fontSize: 18, fontFamily: 'Noto Sans TC', fontWeight: 300, lineHeight: '27px' }}>{label}</span>
        {required && <span style={{ color: '#BC313D', fontSize: 18, fontFamily: 'Noto Sans TC', fontWeight: 300, lineHeight: '27px' }}>*</span>}
      </div>
      <span style={{ flex: 1, color: '#6E625B', fontSize: 18, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '27px' }}>
        {value || emptyText}
      </span>
    </div>
  );
}

// ─── Tab 按鈕 ─────────────────────────────────────────────────────────────────
function TabButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex-1 flex items-center justify-center"
      style={{
        height: 40,
        background: active ? 'rgba(0,70,130,0.60)' : 'rgba(110,98,91,0.04)',
        boxShadow: '0px 11px 23px rgba(0,0,0,0.10), 0px 42px 42px rgba(0,0,0,0.09)',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        border: 'none',
        cursor: 'pointer',
        gap: 10,
      }}
    >
      <span style={{ color: active ? 'white' : '#6E625B', fontSize: 'clamp(12px, 3vw, 18px)', fontFamily: 'Noto Sans TC', fontWeight: active ? 500 : 300, lineHeight: '1.4', textAlign: 'center', padding: '0 4px' }}>
        {label}
      </span>
    </button>
  );
}

// ─── Header 積分條（subMenu 用） ──────────────────────────────────────────────
function HeaderScoreBar({ available, total }) {
  const { t } = useLanguage();
  const pct = total > 0 ? Math.min(100, (available / total) * 100) : 0;
  return (
    <div
      style={{
        width: '100%',
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 4,
        paddingBottom: 8,
        background: 'rgba(255,255,255,0.75)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div className="flex items-baseline justify-between" style={{ marginBottom: 4 }}>
        <span style={{ color: 'rgba(110,98,91,0.60)', fontSize: 13, fontFamily: 'Noto Sans TC', fontWeight: 400, lineHeight: '19.5px' }}>
          {t('profile.scoreLabel')}
        </span>
        <div className="flex items-baseline gap-1">
          <span style={{ color: '#C59B5A', fontSize: 20, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '32px', letterSpacing: 0.38 }}>
            {available.toLocaleString()}
          </span>
          <span style={{ color: 'rgba(110,98,91,0.30)', fontSize: 20, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '32px' }}>/</span>
          <span style={{ color: '#6E625B', fontSize: 20, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '32px', letterSpacing: 0.38 }}>
            {total.toLocaleString()}
          </span>
        </div>
      </div>
      <div className="rounded-full overflow-hidden" style={{ height: 8, background: 'rgba(110,98,91,0.12)' }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: '#BC313D' }}
        />
      </div>
    </div>
  );
}

// ─── 主頁面 ───────────────────────────────────────────────────────────────────
const ProfilePage = () => {
  const navigate = useNavigate();
  const { setLayoutOptions } = usePageLayout();
  const { t, currentLanguage } = useLanguage();
  const userInfo = getUserInfo();

  const TABS = [t('profile.tabAboutMe'), t('profile.tabTreasureBag'), t('profile.tabActivityPoints')];

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [activityItems, setActivityItems] = useState([]);
  const [activityTotalPoints, setActivityTotalPoints] = useState(0);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFetched, setActivityFetched] = useState(false);
  const [lightingItems, setLightingItems] = useState([]);
  const [lightingTotalPoints, setLightingTotalPoints] = useState(0);
  const [lightingLoading, setLightingLoading] = useState(false);
  const [transmissionItems, setTransmissionItems] = useState([]);
  const [transmissionTotalPoints, setTransmissionTotalPoints] = useState(0);
  const [transmissionLoading, setTransmissionLoading] = useState(false);

  const [boardItems, setBoardItems] = useState([]);
  const [boardTotalPoints, setBoardTotalPoints] = useState(0);
  const [boardLoading, setBoardLoading] = useState(false);

  const [quizItems, setQuizItems] = useState([]);
  const [quizTotalPoints, setQuizTotalPoints] = useState(0);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizFetched, setQuizFetched] = useState(false);

  const [collectedMascots, setCollectedMascots] = useState([]);
  const [mascotItems, setMascotItems] = useState([]);
  const [mascotTotalPoints, setMascotTotalPoints] = useState(0);
  const [mascotLoading, setMascotLoading] = useState(false);
  const [mascotFetched, setMascotFetched] = useState(false);

  const [vowItems, setVowItems] = useState([]);
  const [vowTotalPoints, setVowTotalPoints] = useState(0);
  const [vowLoading, setVowLoading] = useState(false);
  const [vowFetched, setVowFetched] = useState(false);

  const [dnaItems, setDnaItems] = useState([]);
  const [dnaLoading, setDnaLoading] = useState(false);
  const [dnaFetched, setDnaFetched] = useState(false);
  const [dnaTotalPoints, setDnaTotalPoints] = useState(0);

  const [postcardItem, setPostcardItem] = useState(null);
  const [postcardLoading, setPostcardLoading] = useState(false);
  const [postcardFetched, setPostcardFetched] = useState(false);

  const [wishTreeItem, setWishTreeItem] = useState(null);
  const [wishTreeLoading, setWishTreeLoading] = useState(false);
  const [wishTreeFetched, setWishTreeFetched] = useState(false);

  const [exchangeItems, setExchangeItems] = useState([]);
  const [exchangeTotalPoints, setExchangeTotalPoints] = useState(0);
  const [exchangeLoading, setExchangeLoading] = useState(false);

  const [shadowItems, setShadowItems] = useState([]);
  const [shadowTotalPoints, setShadowTotalPoints] = useState(0);
  const [shadowLoading, setShadowLoading] = useState(false);

  const [isRuleOpen, setIsRuleOpen] = useState(false);
  const [ruleTarget, setRuleTarget] = useState(null);

  const [isVowListOpen, setIsVowListOpen] = useState(false);
  const [vowListContentId, setVowListContentId] = useState(null);
  const [vowListCategoryName, setVowListCategoryName] = useState('');

  const [isBoardDetailOpen, setIsBoardDetailOpen] = useState(false);
  const [selectedBoardId, setSelectedBoardId] = useState(null);

  const [isPostcardListOpen, setIsPostcardListOpen] = useState(false);
  const [isWishTreeListOpen, setIsWishTreeListOpen] = useState(false);

  useEffect(() => {
    setActivityFetched(false);
    setQuizFetched(false);
    setMascotFetched(false);
    setVowFetched(false);
    setDnaFetched(false);
    setPostcardFetched(false);
  }, [currentLanguage]);

  useEffect(() => {
    if (!userInfo) {
      navigate(ROUTES.LOGIN, { replace: true });
      return;
    }
    getMyProfile()
      .then(setProfile)
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
    getMascotList({ is_collected: true })
      .then((res) => {
        if (res?.items) {
          setMascotItems(res.items);
          setCollectedMascots(res.items);
        }
        if (res?.settled_point) {
          setMascotTotalPoints(res.settled_point);
        }
      })
      .catch((err) => console.error('Failed to fetch mascots:', err))
      .finally(() => setMascotLoading(false));
  }, [currentLanguage]);

  useEffect(() => {
    if (loading) return;
    const available = profile?.available_point ?? 0;
    const total = profile?.total_point ?? 0;

    setLayoutOptions({
      headerSubMenu: activeTab !== 0 ? (
        <HeaderScoreBar available={available} total={total} />
      ) : null,
      headerHeight: activeTab === 0 ? 70 : 140,
    });
    return () => setLayoutOptions({});
  }, [loading, profile, activeTab]);

  useEffect(() => {
    if (activeTab === 2 && !activityFetched) {
      setActivityFetched(true);

      setActivityLoading(true);
      getActivityList()
        .then((res) => {
          setActivityItems(res?.items ?? []);
          setActivityTotalPoints(res?.settled_point ?? 0);
        })
        .catch(() => setActivityItems([]))
        .finally(() => setActivityLoading(false));

      setLightingLoading(true);
      getLightingPostList()
        .then((res) => {
          setLightingItems(res?.items ?? []);
          setLightingTotalPoints(res?.point ?? 0);
        })
        .catch(() => setLightingItems([]))
        .finally(() => setLightingLoading(false));

      setTransmissionLoading(true);
      getExhibitionPostList()
        .then((res) => {
          setTransmissionItems(res?.items ?? []);
          setTransmissionTotalPoints(res?.point ?? 0);
        })
        .catch(() => setTransmissionItems([]))
        .finally(() => setTransmissionLoading(false));

      setBoardLoading(true);
      getBoardList()
        .then((res) => {
          setBoardItems(res?.items ?? []);
          setBoardTotalPoints(res?.settled_point ?? 0);
        })
        .catch(() => setBoardItems([]))
        .finally(() => setBoardLoading(false));

      setExchangeLoading(true);
      getPointTxList()
        .then((res) => {
          setExchangeItems(res?.items ?? []);
          setExchangeTotalPoints(res?.total_points ?? 0);
        })
        .catch(() => setExchangeItems([]))
        .finally(() => setExchangeLoading(false));

      setShadowLoading(true);
      getShadowList()
        .then((res) => {
          setShadowItems((res?.items ?? []).filter(item => item.record_id != null));
          setShadowTotalPoints(res?.settled_point ?? 0);
        })
        .catch(() => setShadowItems([]))
        .finally(() => setShadowLoading(false));
    }
  }, [activeTab, activityFetched, currentLanguage]);

  useEffect(() => {
    if (activeTab === 1) {
      if (!quizFetched) {
        setQuizFetched(true);
        setQuizLoading(true);
        setTimeout(() => {
          getBuddhistList({})
            .then((res) => {
              if (res?.items && res.items.length > 0) setQuizItems(res.items);
              if (res?.settled_point) setQuizTotalPoints(res.settled_point);
            })
            .catch((err) => console.error('Failed to fetch lighting post:', err))
            .finally(() => setQuizLoading(false));
        }, 500);
      }

      if (!mascotFetched) {
        setMascotFetched(true);
        setMascotLoading(true);
        getMascotList()
          .then((res) => {
            if (res?.items) setMascotItems(res.items);
            if (res?.settled_point) setMascotTotalPoints(res.settled_point);
          })
          .catch((err) => console.error('Failed to fetch mascots:', err))
          .finally(() => setMascotLoading(false));
      }

      if (!vowFetched) {
        setVowFetched(true);
        setVowLoading(true);
        getVowList()
          .then((res) => {
            if (res?.items) setVowItems(res.items);
            if (res?.settled_point) setVowTotalPoints(res.settled_point);
          })
          .catch((err) => console.error('Failed to fetch vows:', err))
          .finally(() => setVowLoading(false));
      }

      if (!dnaFetched) {
        setDnaFetched(true);
        setDnaLoading(true);
        getDnaList()
          .then((res) => {
            const fetchedItems = Array.isArray(res) ? res : res?.items ? res.items : (res?.archetype_id ? [res] : []);
            setDnaItems(fetchedItems);
            setDnaTotalPoints(res?.point);
          })
          .catch((err) => console.error('Failed to fetch dna:', err))
          .finally(() => setDnaLoading(false));
      }

      if (!postcardFetched) {
        setPostcardFetched(true);
        setPostcardLoading(true);
        getPostcardDescribe()
          .then((res) => setPostcardItem(res))
          .catch((err) => console.error('Failed to fetch postcard:', err))
          .finally(() => setPostcardLoading(false));
      }

      if (!wishTreeFetched) {
        setWishTreeFetched(true);
        setWishTreeLoading(true);
        getExhibitionVowDescribe()
          .then((res) => {
            // console.log('getExhibitionVowDescribe', res);
            setWishTreeItem(res);
          })
          .catch((err) => console.error('Failed to fetch wish tree:', err))
          .finally(() => setWishTreeLoading(false));
      }
    }
  }, [activeTab, quizFetched, mascotFetched, vowFetched, dnaFetched, postcardFetched, wishTreeFetched, currentLanguage]);

  if (!userInfo) return null;

  if (loading) {
    return <div className="flex justify-center py-20 text-stone-300 text-sm">{t('profile.loading')}</div>;
  }

  const name = profile?.user_name || userInfo.name || '';
  const birthday = profile?.birthday || userInfo.birthday || '';
  const dharmaName = profile?.dharma_name || userInfo.dharmaName || '';
  const monastery = profile?.monastery_name || userInfo.monasteryName || '';
  const mascotUrl = profile?.mascot_url || null;
  const mascotName = profile?.mascot_name || null;
  const available = profile?.available_point ?? 0;
  const total = profile?.total_point ?? 0;
  const createdAt = profile?.created_at
    ? new Date(profile.created_at).toISOString().slice(0, 10)
    : '';

  return (
    <div className="flex flex-col px-3 py-8 max-w-lg mx-auto w-full" style={{ gap: 0 }}>
      {/* Tab Row */}
      <div className="flex items-center">
        {TABS.map((label, i) => (
          <TabButton key={label} label={label} active={activeTab === i} onClick={() => setActiveTab(i)} />
        ))}
      </div>

      {/* Contents Card */}
      <div
        className="flex flex-col items-center"
        style={{
          alignSelf: 'stretch',
          padding: 24,
          background: 'rgba(255,255,255,0.80)',
          boxShadow: '0px 11px 23px rgba(0,0,0,0.10), 0px 42px 42px rgba(0,0,0,0.09), 0px 95px 57px rgba(0,0,0,0.05)',
          borderBottomRightRadius: 20,
          borderBottomLeftRadius: 20,
          gap: 16,
        }}
      >
        {/* 關於我 */}
        {activeTab === 0 && (
          <>
            <MascotSection
              mascotUrl={mascotUrl}
              mascotName={mascotName}
              userName={name}
              collectedMascots={collectedMascots}
              onAvatarUpdate={(reward_id) => {
                updateProfileAvatar({ reward_id }).then(() => {
                  getMyProfile().then(setProfile);
                }).catch(err => console.error('Failed to update avatar:', err));
              }}
            />
            <ScoreBar available={available} total={total} />
            <div className="flex flex-col w-full" style={{ gap: 8 }}>
              <InfoRow label={t('profile.nameLabel')} value={name} emptyText={t('profile.emptyValue')} />
              <InfoRow label={t('profile.birthdayLabel')} value={birthday} required emptyText={t('profile.emptyValue')} />
              <InfoRow label={t('profile.dharmaNameLabel')} value={dharmaName} showEmpty emptyText={t('profile.emptyValue')} />
              <InfoRow label={t('profile.monasteryLabel')} value={monastery} emptyText={t('profile.emptyValue')} />
              {createdAt && <InfoRow label={t('profile.registeredAtLabel')} value={createdAt} emptyText={t('profile.emptyValue')} />}
            </div>
          </>
        )}

        {/* 百寶袋 */}
        {activeTab === 1 && (
          <TreasureBagTab
            quizItems={quizItems}
            quizLoading={quizLoading}
            quizTotalPoints={quizTotalPoints}
            mascotItems={mascotItems}
            mascotLoading={mascotLoading}
            mascotTotalPoints={mascotTotalPoints}
            vowItems={vowItems}
            vowLoading={vowLoading}
            vowTotalPoints={vowTotalPoints}
            dnaItems={dnaItems}
            dnaLoading={dnaLoading}
            dnaTotalPoints={dnaTotalPoints}
            postcardItem={postcardItem}
            postcardLoading={postcardLoading}
            wishTreeItem={wishTreeItem}
            wishTreeLoading={wishTreeLoading}
            onRuleClick={(target) => {
              setRuleTarget(target);
              setIsRuleOpen(true);
            }}
            onVowItemClick={({ contentId, categoryName }) => {
              setVowListContentId(contentId);
              setVowListCategoryName(categoryName);
              setIsVowListOpen(true);
            }}
            onPostcardItemClick={() => {
              setIsPostcardListOpen(true);
            }}
            onWishTreeItemClick={() => {
              setIsWishTreeListOpen(true);
            }}
          />
        )}

        {/* 活動積分 */}
        {activeTab === 2 && (
          <ActivityPointsTab
            boardItems={boardItems}
            boardTotalPoints={boardTotalPoints}
            boardLoading={boardLoading}
            activityItems={activityItems}
            activityTotalPoints={activityTotalPoints}
            activityLoading={activityLoading}
            lightingItems={lightingItems}
            lightingTotalPoints={lightingTotalPoints}
            lightingLoading={lightingLoading}
            transmissionItems={transmissionItems}
            transmissionTotalPoints={transmissionTotalPoints}
            transmissionLoading={transmissionLoading}
            exchangeItems={exchangeItems}
            exchangeTotalPoints={exchangeTotalPoints}
            exchangeLoading={exchangeLoading}
            shadowItems={shadowItems}
            shadowTotalPoints={shadowTotalPoints}
            shadowLoading={shadowLoading}
            onBoardClick={(boardId) => {
              setSelectedBoardId(boardId);
              setIsBoardDetailOpen(true);
            }}
            onShadowClick={(topicId) => {
              navigate(ROUTES.JUE_GUANG_TOPIC(topicId), { state: { fromProfile: true } });
            }}
            onRuleClick={(target) => {
              setRuleTarget(target);
              setIsRuleOpen(true);
            }}
          />
        )}
      </div>

      <RulePages isOpen={isRuleOpen} onClose={() => setIsRuleOpen(false)} target={ruleTarget} />

      <VowList
        isOpen={isVowListOpen}
        onClose={() => setIsVowListOpen(false)}
        contentId={vowListContentId}
        categoryName={vowListCategoryName}
      />
      <PostcardList
        isOpen={isPostcardListOpen}
        onClose={() => setIsPostcardListOpen(false)}
      />
      <WishTreeList
        isOpen={isWishTreeListOpen}
        onClose={() => setIsWishTreeListOpen(false)}
      />
      <BoardDetailPopup
        isOpen={isBoardDetailOpen}
        onClose={() => setIsBoardDetailOpen(false)}
        boardId={selectedBoardId}
      />
    </div>
  );
};

export default ProfilePage;
